/**
 * The picture around a structure: the metro's lines, the timeline's axis, the matrix's cells, the
 * board's lanes, the road, and the links between layers.
 *
 * Built from where the nodes *are*, every time the scene is, never from where a layout last put
 * them — so a station somebody dragged takes its lines with it, a card dropped lower stretches its
 * lane, and a milestone moved along keeps its road. What this returns is plain scene data:
 *
 * - an **underlay** of primitives drawn under everything else, in the page's colours (`'ink'` is
 *   the page's text colour, `'surface'` the page itself);
 * - **patches** to the nodes that play a part — a station is a dot with its name beside it, a
 *   heading is bold text with no box, a line's badge is filled with the line's colour;
 * - which **hierarchy links** still show: none on a metro map (the lines are the links), none from
 *   the title to what it arranges, and a list's elbow for details hanging under an item.
 *
 * Colours: a branch somebody coloured keeps its colour; otherwise it takes the validated chart
 * palette in order — distinct for the common kinds of colour blindness, which is exactly what a
 * set of metro lines needs.
 */
import { childrenOf } from '@orbit-mind/domain';
import { CELL_PAD, LANE_MIN_WIDTH, MIN_CELL, METRO_DOT, ROAD_RISE, metroLines, stationDot, stationKey, } from '@orbit-mind/layout-engine';
import { CHART_PALETTE, isDarkColor } from './paint';
/** The thickness of a metro line, and of the page-coloured casing that lets two lines cross. */
export const METRO_LINE_WIDTH = 6;
const METRO_CASING = 3;
/** A branch's colour: the one somebody gave it, or the next in the chart palette. */
export function structureColour(node, index) {
    const palette = CHART_PALETTE.light;
    return (node?.style.stroke ??
        node?.style.fill ??
        palette[index % palette.length] ??
        '#2a78d6');
}
/** White or near-black, whichever reads on `fill`. */
function inkOn(fill) {
    return (isDarkColor(fill) ? '#ffffff' : '#1f2330');
}
function polyline(points) {
    return points.map((to, index) => ({ kind: index === 0 ? 'move' : 'line', to }));
}
function union(a, b) {
    const x = Math.min(a.x, b.x);
    const y = Math.min(a.y, b.y);
    return {
        x,
        y,
        width: Math.max(a.x + a.width, b.x + b.width) - x,
        height: Math.max(a.y + a.height, b.y + b.height) - y,
    };
}
function grow(rect, by) {
    return {
        x: rect.x - by,
        y: rect.y - by,
        width: rect.width + by * 2,
        height: rect.height + by * 2,
    };
}
const centre = (rect) => ({ x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 });
/** An arrowhead pointing along `direction` with its tip at `tip`. */
function arrowHead(tip, direction, size) {
    const back = { x: tip.x - direction.x * size, y: tip.y - direction.y * size };
    const normal = { x: -direction.y * size * 0.6, y: direction.x * size * 0.6 };
    return polyline([
        tip,
        { x: back.x + normal.x, y: back.y + normal.y },
        { x: back.x - normal.x, y: back.y - normal.y },
        tip,
    ]);
}
/** A node's style with the fill, and the text colour chosen for that fill, taken off. */
function withoutFill(style) {
    const { fill: _fill, textColor: _text, ...rest } = style;
    return rest;
}
/** Bold text with no box: a heading of a cell, a lane or a layer. */
function heading(node, colour) {
    const text = node.textBox ?? node.rect;
    return {
        bare: true,
        style: withoutFill(node.style),
        textStyle: { ...node.textStyle, fontWeight: 700 },
        glyph: [
            {
                kind: 'rect',
                rect: {
                    x: text.x + text.width / 2 - 14,
                    y: text.y + text.height - 4,
                    width: 28,
                    height: 3,
                },
                radius: 1.5,
                fill: colour,
            },
        ],
    };
}
/** The rectangle every primitive in a list covers, roughly for text. */
export function primitivesBounds(primitives) {
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    const consider = (x, y, pad = 0) => {
        minX = Math.min(minX, x - pad);
        minY = Math.min(minY, y - pad);
        maxX = Math.max(maxX, x + pad);
        maxY = Math.max(maxY, y + pad);
    };
    for (const primitive of primitives) {
        switch (primitive.kind) {
            case 'rect':
            case 'image':
                consider(primitive.rect.x, primitive.rect.y);
                consider(primitive.rect.x + primitive.rect.width, primitive.rect.y + primitive.rect.height);
                break;
            case 'circle':
                consider(primitive.cx, primitive.cy, primitive.r + (primitive.strokeWidth ?? 0) / 2);
                break;
            case 'text':
                consider(primitive.x, primitive.y, primitive.size * Math.max(1, primitive.text.length) * 0.3);
                break;
            case 'path': {
                const pad = (primitive.strokeWidth ?? 0) / 2;
                for (const segment of primitive.segments) {
                    consider(segment.to.x, segment.to.y, pad);
                    if (segment.kind === 'cubic') {
                        consider(segment.control1.x, segment.control1.y, pad);
                        consider(segment.control2.x, segment.control2.y, pad);
                    }
                }
                break;
            }
        }
    }
    if (!Number.isFinite(minX))
        return null;
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}
function toolsFor(input) {
    const byId = new Map(input.nodes.map((node) => [node.id, node]));
    const root = byId.get(input.document.map.rootNodeId);
    if (root === undefined)
        return null;
    const children = (nodeId) => childrenOf(input.document, nodeId).flatMap((child) => {
        const drawn = byId.get(child.id);
        return drawn === undefined ? [] : [drawn];
    });
    const subtree = (nodeId) => {
        const node = byId.get(nodeId);
        let rect = node?.rect ?? { x: 0, y: 0, width: 0, height: 0 };
        for (const child of children(nodeId))
            rect = union(rect, subtree(child.id));
        return rect;
    };
    return { root, byId, children, subtree, doc: (nodeId) => input.document.nodes[nodeId] };
}
/** Everything under a node is drawn as an outline's elbow; the title's own links are not drawn. */
function titleArranges(rootId, depthOf, flushItems) {
    return (parentId) => {
        if (parentId === rootId)
            return 'none';
        // A cell's, a lane's or a layer's own items sit in it, joined by nothing but the cell.
        if (flushItems && depthOf(parentId) === 1)
            return 'none';
        return 'list';
    };
}
export function structureScene(family, input) {
    const tools = toolsFor(input);
    if (tools === null)
        return { nodes: input.nodes, underlay: [], link: () => 'usual' };
    const patches = new Map();
    const underlay = [];
    const depthOf = (nodeId) => tools.byId.get(nodeId)?.depth ?? 0;
    let link;
    switch (family) {
        case 'metro':
            metro(input, tools, patches, underlay);
            link = () => 'none';
            break;
        case 'timeline':
            timeline(tools, patches, underlay);
            link = titleArranges(tools.root.id, depthOf, false);
            break;
        case 'matrix':
            matrix(tools, patches, underlay);
            link = titleArranges(tools.root.id, depthOf, true);
            break;
        case 'board':
            board(tools, patches, underlay);
            link = titleArranges(tools.root.id, depthOf, true);
            break;
        case 'road':
            road(tools, patches, underlay);
            link = titleArranges(tools.root.id, depthOf, false);
            break;
        case 'layers':
            layers(tools, patches, underlay);
            link = titleArranges(tools.root.id, depthOf, true);
            break;
    }
    return {
        nodes: input.nodes.map((node) => {
            const patch = patches.get(node.id);
            return patch === undefined ? node : { ...node, ...patch };
        }),
        underlay,
        link,
    };
}
function timeline(tools, patches, underlay) {
    const { root } = tools;
    const axisY = root.rect.y + root.rect.height / 2;
    const events = tools.children(root.id);
    const start = root.rect.x + root.rect.width + 12;
    let end = start + 60;
    for (const node of tools.byId.values()) {
        if (node.id !== root.id)
            end = Math.max(end, node.rect.x + node.rect.width + 28);
    }
    underlay.push({
        kind: 'path',
        segments: polyline([
            { x: start, y: axisY },
            { x: end, y: axisY },
        ]),
        stroke: 'ink',
        strokeWidth: 3,
        opacity: 0.25,
    }, {
        kind: 'path',
        segments: arrowHead({ x: end + 12, y: axisY }, { x: 1, y: 0 }, 12),
        fill: 'ink',
        opacity: 0.25,
    });
    events.forEach((event, index) => {
        const colour = structureColour(tools.doc(event.id), index);
        const box = tools.subtree(event.id);
        const x = centre(event.rect).x;
        const above = centre(event.rect).y < axisY;
        underlay.push({
            kind: 'path',
            segments: polyline([
                { x, y: axisY },
                { x, y: above ? box.y + box.height : box.y },
            ]),
            stroke: colour,
            strokeWidth: 1.5,
        }, {
            kind: 'circle',
            cx: x,
            cy: axisY,
            r: 6.5,
            fill: colour,
            stroke: 'surface',
            strokeWidth: 2.5,
        });
        patches.set(event.id, { style: { ...event.style, stroke: colour, strokeWidth: 2 } });
    });
}
/** Rows and columns of cells, lined up: every cell in a column as wide, every row as tall. */
function grid(boxes, columns, minimum) {
    const lefts = [];
    const rights = [];
    const tops = [];
    const bottoms = [];
    boxes.forEach((box, index) => {
        const column = index % columns;
        const row = Math.floor(index / columns);
        lefts[column] = Math.min(lefts[column] ?? box.x, box.x);
        rights[column] = Math.max(rights[column] ?? box.x + box.width, box.x + box.width);
        tops[row] = Math.min(tops[row] ?? box.y, box.y);
        bottoms[row] = Math.max(bottoms[row] ?? box.y + box.height, box.y + box.height);
    });
    return boxes.map((_, index) => {
        const column = index % columns;
        const row = Math.floor(index / columns);
        const x = lefts[column] ?? 0;
        const y = tops[row] ?? 0;
        return {
            x,
            y,
            width: Math.max(minimum.width, (rights[column] ?? x) - x),
            height: Math.max(minimum.height, (bottoms[row] ?? y) - y),
        };
    });
}
function matrix(tools, patches, underlay) {
    const heads = tools.children(tools.root.id);
    if (heads.length === 0)
        return;
    const columns = heads.length === 1 ? 1 : 2;
    const cells = grid(heads.map((head) => grow(tools.subtree(head.id), CELL_PAD)), columns, MIN_CELL);
    heads.forEach((head, index) => {
        const colour = structureColour(tools.doc(head.id), index);
        const cell = cells[index];
        if (cell === undefined)
            return;
        underlay.push({ kind: 'rect', rect: cell, radius: 14, fill: colour, opacity: 0.08 }, { kind: 'rect', rect: cell, radius: 14, stroke: colour, strokeWidth: 1.2, opacity: 0.45 });
        patches.set(head.id, heading(head, colour));
    });
    // The two axes of a 2×2, through the gaps between the cells.
    const [a, b, c, d] = cells;
    if (a !== undefined && b !== undefined && c !== undefined) {
        const x = (a.x + a.width + b.x) / 2;
        const y = (a.y + a.height + c.y) / 2;
        const bottom = Math.max(c.y + c.height, d === undefined ? 0 : d.y + d.height) + 14;
        const right = b.x + b.width + 14;
        const left = a.x - 14;
        const top = a.y - 14;
        underlay.push({
            kind: 'path',
            segments: polyline([
                { x, y: bottom },
                { x, y: top },
            ]),
            stroke: 'ink',
            strokeWidth: 1.5,
            opacity: 0.35,
        }, {
            kind: 'path',
            segments: arrowHead({ x, y: top - 8 }, { x: 0, y: -1 }, 9),
            fill: 'ink',
            opacity: 0.35,
        }, {
            kind: 'path',
            segments: polyline([
                { x: left, y },
                { x: right, y },
            ]),
            stroke: 'ink',
            strokeWidth: 1.5,
            opacity: 0.35,
        }, {
            kind: 'path',
            segments: arrowHead({ x: right + 8, y }, { x: 1, y: 0 }, 9),
            fill: 'ink',
            opacity: 0.35,
        });
    }
}
function board(tools, patches, underlay) {
    const lanes = tools.children(tools.root.id);
    if (lanes.length === 0)
        return;
    const boxes = lanes.map((lane) => grow(tools.subtree(lane.id), CELL_PAD));
    const top = Math.min(...boxes.map((box) => box.y));
    const bottom = Math.max(...boxes.map((box) => box.y + box.height));
    const width = Math.max(LANE_MIN_WIDTH, ...boxes.map((box) => box.width));
    lanes.forEach((lane, index) => {
        const colour = structureColour(tools.doc(lane.id), index);
        const box = boxes[index];
        if (box === undefined)
            return;
        const rect = { x: box.x, y: top, width, height: bottom - top };
        underlay.push({ kind: 'rect', rect, radius: 14, fill: 'ink', opacity: 0.045 }, {
            kind: 'rect',
            rect: { x: rect.x + 14, y: rect.y, width: rect.width - 28, height: 4 },
            radius: 2,
            fill: colour,
        });
        patches.set(lane.id, heading(lane, colour));
    });
}
function road(tools, patches, underlay) {
    const { root } = tools;
    const stops = tools.children(root.id);
    if (stops.length === 0)
        return;
    const start = { x: root.rect.x + root.rect.width + 4, y: root.rect.y + root.rect.height / 2 };
    const points = stops.map((stop) => ({
        x: centre(stop.rect).x,
        y: (() => {
            const box = tools.subtree(stop.id);
            return box.y + box.height + ROAD_RISE;
        })(),
    }));
    const segments = [{ kind: 'move', to: start }];
    let previous = start;
    let heading = 1;
    for (const point of points) {
        const rise = Math.abs(point.y - previous.y);
        if (rise < 30) {
            segments.push({ kind: 'line', to: point });
            if (Math.abs(point.x - previous.x) > 1)
                heading = Math.sign(point.x - previous.x);
        }
        else {
            // A turn: out past the end of the row and back, the way a road winds down a hill.
            const reach = rise * 0.7;
            segments.push({
                kind: 'cubic',
                control1: { x: previous.x + heading * reach, y: previous.y },
                control2: { x: point.x + heading * reach, y: point.y },
                to: point,
            });
            heading = -heading;
        }
        previous = point;
    }
    const finish = { x: previous.x + heading * 40, y: previous.y };
    segments.push({ kind: 'line', to: finish });
    underlay.push({ kind: 'path', segments, stroke: 'ink', strokeWidth: 26, opacity: 0.13 }, { kind: 'path', segments, stroke: 'surface', strokeWidth: 2, dash: [9, 9] }, { kind: 'circle', cx: start.x, cy: start.y, r: 6, fill: 'ink', opacity: 0.45 }, 
    // The finish: a flag on a pole.
    {
        kind: 'path',
        segments: polyline([finish, { x: finish.x, y: finish.y - 30 }]),
        stroke: 'ink',
        strokeWidth: 2,
    }, {
        kind: 'path',
        segments: polyline([
            { x: finish.x, y: finish.y - 30 },
            { x: finish.x + heading * 18, y: finish.y - 24 },
            { x: finish.x, y: finish.y - 18 },
        ]),
        fill: 'ink',
    });
    stops.forEach((stop, index) => {
        const colour = structureColour(tools.doc(stop.id), index);
        const point = points[index];
        if (point === undefined)
            return;
        underlay.push({
            kind: 'path',
            segments: polyline([
                { x: point.x, y: point.y - 12 },
                { x: point.x, y: point.y - ROAD_RISE },
            ]),
            stroke: colour,
            strokeWidth: 1.5,
        }, {
            kind: 'circle',
            cx: point.x,
            cy: point.y,
            r: 12,
            fill: colour,
            stroke: 'surface',
            strokeWidth: 3,
        }, {
            kind: 'text',
            x: point.x,
            y: point.y + 4,
            text: String(index + 1),
            size: 11,
            weight: 700,
            anchor: 'middle',
            fill: inkOn(colour),
        });
        patches.set(stop.id, { style: { ...stop.style, stroke: colour, strokeWidth: 2 } });
    });
}
function layers(tools, patches, underlay) {
    const heads = tools.children(tools.root.id);
    const items = heads.map((head) => tools.children(head.id));
    for (let index = 1; index < heads.length; index += 1) {
        for (const from of items[index - 1] ?? []) {
            for (const to of items[index] ?? []) {
                const a = { x: from.rect.x + from.rect.width, y: centre(from.rect).y };
                const b = { x: to.rect.x, y: centre(to.rect).y };
                const bend = (b.x - a.x) / 2;
                underlay.push({
                    kind: 'path',
                    segments: [
                        { kind: 'move', to: a },
                        {
                            kind: 'cubic',
                            control1: { x: a.x + bend, y: a.y },
                            control2: { x: b.x - bend, y: b.y },
                            to: b,
                        },
                    ],
                    stroke: 'ink',
                    strokeWidth: 1.2,
                    opacity: 0.22,
                });
            }
        }
    }
    heads.forEach((head, index) => {
        const colour = structureColour(tools.doc(head.id), index);
        patches.set(head.id, heading(head, colour));
        for (const item of items[index] ?? []) {
            patches.set(item.id, { style: { ...item.style, stroke: colour } });
        }
    });
}
/** Where a station's name goes, as a direction from its dot, in order of preference. */
const SIDES = [
    { x: 1, y: 0 },
    { x: 0, y: 1 },
    { x: 0, y: -1 },
    { x: -1, y: 0 },
    { x: Math.SQRT1_2, y: Math.SQRT1_2 },
    { x: Math.SQRT1_2, y: -Math.SQRT1_2 },
    { x: -Math.SQRT1_2, y: Math.SQRT1_2 },
    { x: -Math.SQRT1_2, y: -Math.SQRT1_2 },
];
/**
 * The side of a station its name goes on: the one furthest from every line leaving it. A station
 * on a straight stretch across has both sides free; every other one takes the top, so two names
 * side by side never touch.
 */
function nameSide(dot, neighbours, alternate) {
    const leaving = neighbours
        .map((point) => {
        const dx = point.x - dot.x;
        const dy = point.y - dot.y;
        const length = Math.hypot(dx, dy);
        return length < 0.5 ? null : { x: dx / length, y: dy / length };
    })
        .filter((direction) => direction !== null);
    const order = alternate ? [SIDES[0], SIDES[2], SIDES[1], ...SIDES.slice(3)] : SIDES;
    let best = SIDES[0] ?? { x: 1, y: 0 };
    let bestScore = -1;
    for (const side of order) {
        if (side === undefined)
            continue;
        const score = leaving.length === 0
            ? Math.PI
            : Math.min(...leaving.map((direction) => Math.acos(Math.max(-1, Math.min(1, side.x * direction.x + side.y * direction.y)))));
        if (score > bestScore + 1e-6) {
            bestScore = score;
            best = side;
        }
    }
    return best;
}
function nameRect(dot, side, size) {
    const gap = METRO_DOT / 2 + 5;
    const x = side.x > 0.1
        ? dot.x + gap * Math.abs(side.x)
        : side.x < -0.1
            ? dot.x - gap * Math.abs(side.x) - size.width
            : dot.x - size.width / 2;
    const y = side.y > 0.1
        ? dot.y + gap * Math.abs(side.y) - 2
        : side.y < -0.1
            ? dot.y - gap * Math.abs(side.y) - size.height + 2
            : dot.y - size.height / 2;
    return { x, y, width: size.width, height: size.height };
}
/** The unit vector from `a` towards `b`, or `null` when they are the same point. */
function towards(a, b) {
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    return length < 0.5 ? null : { x: (b.x - a.x) / length, y: (b.y - a.y) / length };
}
function turn(a, b) {
    if (a === null || b === null)
        return 0;
    return Math.acos(Math.max(-1, Math.min(1, a.x * b.x + a.y * b.y)));
}
/**
 * Where a stretch of metro line between two stations bends, so that it runs only across, up or at
 * 45 degrees — or `null` when it already does.
 *
 * Two ways to bend: diagonal first, or straight first. The one taken runs on from the stretches
 * either side of it (a line keeps its heading through a station) and never leaves or reaches a
 * station along another line's track, which would draw one line on top of another.
 */
function octilinearBend(from, to, context) {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const diagonal = Math.min(Math.abs(dx), Math.abs(dy));
    if (diagonal < 1 || Math.abs(Math.abs(dx) - Math.abs(dy)) < 1)
        return null;
    const options = [
        { x: from.x + Math.sign(dx) * diagonal, y: from.y + Math.sign(dy) * diagonal },
        { x: to.x - Math.sign(dx) * diagonal, y: to.y - Math.sign(dy) * diagonal },
    ];
    const incoming = context.before === undefined ? null : towards(context.before, from);
    const outgoing = context.after === undefined ? null : towards(to, context.after);
    const alongTrack = (station, direction, others, skip) => others.some((other) => {
        if (Math.hypot(other.x - skip.x, other.y - skip.y) < 0.5)
            return false;
        return turn(direction, towards(station, other)) < 0.09;
    });
    let best = null;
    let bestScore = Number.POSITIVE_INFINITY;
    for (const bend of options) {
        const leave = towards(from, bend);
        const arrive = towards(bend, to);
        const back = arrive === null ? null : { x: -arrive.x, y: -arrive.y };
        const score = turn(incoming, leave) +
            turn(arrive, outgoing) +
            (alongTrack(from, leave, context.leavingFrom, to) ? 10 : 0) +
            (alongTrack(to, back, context.arrivingAt, from) ? 10 : 0);
        if (score < bestScore - 1e-6) {
            bestScore = score;
            best = bend;
        }
    }
    return best;
}
function metro(input, tools, patches, underlay) {
    const lines = metroLines(input.document)
        .map((entry) => ({
        line: tools.byId.get(entry.line.id),
        docLine: entry.line,
        stations: entry.stations.filter((station) => tools.byId.has(station.id)),
    }))
        .filter((entry) => entry.line !== undefined);
    // One dot per station name: the first node that carries it. Its twins on other lines are drawn
    // as that one station.
    const canonical = new Map();
    const twins = new Map();
    const usage = new Map();
    lines.forEach((entry, index) => {
        for (const station of entry.stations) {
            const key = stationKey(station);
            if (!canonical.has(key))
                canonical.set(key, station);
            twins.set(key, [...(twins.get(key) ?? []), station.id]);
            usage.set(key, (usage.get(key) ?? new Set()).add(index));
        }
    });
    const dotOf = (key) => {
        const station = canonical.get(key);
        return station === undefined ? undefined : stationDot(station);
    };
    const neighbours = new Map();
    const addNeighbour = (key, point) => {
        neighbours.set(key, [...(neighbours.get(key) ?? []), point]);
    };
    const sequences = lines.map((entry) => {
        const keys = [];
        for (const station of entry.stations) {
            const key = stationKey(station);
            if (keys[keys.length - 1] !== key)
                keys.push(key);
        }
        return keys;
    });
    // Stretches two lines share run side by side rather than on top of each other.
    const shared = new Map();
    const stretch = (a, b) => (a < b ? `${a}\u0000${b}` : `${b}\u0000${a}`);
    sequences.forEach((keys, index) => {
        for (let k = 1; k < keys.length; k += 1) {
            const id = stretch(keys[k - 1] ?? '', keys[k] ?? '');
            const users = shared.get(id) ?? [];
            if (!users.includes(index))
                users.push(index);
            shared.set(id, users);
        }
    });
    // Who each station's neighbours are, on every line, before any line is drawn: a bend is chosen
    // so it does not run along another line leaving the same station.
    lines.forEach((entry, index) => {
        const keys = sequences[index] ?? [];
        const badge = entry.line === undefined ? undefined : centre(entry.line.rect);
        keys.forEach((key, k) => {
            const previous = k === 0 ? badge : dotOf(keys[k - 1] ?? '');
            const next = dotOf(keys[k + 1] ?? '');
            if (previous !== undefined)
                addNeighbour(key, previous);
            if (next !== undefined)
                addNeighbour(key, next);
        });
    });
    lines.forEach((entry, index) => {
        const keys = sequences[index] ?? [];
        const line = entry.line;
        if (line === undefined)
            return;
        const colour = structureColour(entry.docLine, index);
        const badge = centre(line.rect);
        const points = [badge];
        keys.forEach((key, k) => {
            const dot = dotOf(key);
            if (dot === undefined)
                return;
            if (k === 0) {
                points.push(dot);
                return;
            }
            const fromKey = keys[k - 1] ?? '';
            const from = dotOf(fromKey);
            if (from === undefined)
                return;
            const before = k === 1 ? badge : dotOf(keys[k - 2] ?? '');
            const after = dotOf(keys[k + 1] ?? '');
            const bend = octilinearBend(from, dot, {
                before,
                after,
                leavingFrom: neighbours.get(fromKey) ?? [],
                arrivingAt: neighbours.get(key) ?? [],
            });
            const users = shared.get(stretch(fromKey, key)) ?? [index];
            const offset = (users.indexOf(index) - (users.length - 1) / 2) * METRO_LINE_WIDTH;
            if (offset === 0) {
                if (bend !== null)
                    points.push(bend);
                points.push(dot);
                return;
            }
            const length = Math.hypot(dot.x - from.x, dot.y - from.y) || 1;
            const normal = { x: -(dot.y - from.y) / length, y: (dot.x - from.x) / length };
            const shift = (point) => ({
                x: point.x + normal.x * offset,
                y: point.y + normal.y * offset,
            });
            points.push(shift(from), ...(bend === null ? [] : [shift(bend)]), shift(dot), dot);
        });
        const segments = polyline(points);
        underlay.push({
            kind: 'path',
            segments,
            stroke: 'surface',
            strokeWidth: METRO_LINE_WIDTH + METRO_CASING * 2,
        }, { kind: 'path', segments, stroke: colour, strokeWidth: METRO_LINE_WIDTH });
        patches.set(line.id, {
            style: { ...line.style, fill: colour, stroke: colour, textColor: inkOn(colour) },
            textStyle: { ...line.textStyle, fontWeight: 700 },
            cornerRadius: line.rect.height / 2,
            outline: null,
        });
    });
    // The stations: a dot, and the name on the side the lines leave free.
    for (const [key, station] of canonical) {
        const node = tools.byId.get(station.id);
        const dot = dotOf(key);
        if (node === undefined || dot === undefined || node.content !== undefined)
            continue;
        const lineIndex = [...(usage.get(key) ?? [])][0] ?? 0;
        const interchange = (usage.get(key)?.size ?? 0) > 1;
        const colour = structureColour(lines[lineIndex]?.docLine, lineIndex);
        const position = sequences[lineIndex]?.indexOf(key) ?? 0;
        const measured = input.text.get(station.id) ?? { width: 40, height: 16 };
        const size = { width: measured.width + 6, height: measured.height + 2 };
        const label = nameRect(dot, nameSide(dot, neighbours.get(key) ?? [], position % 2 === 1), size);
        const square = {
            x: dot.x - METRO_DOT / 2,
            y: dot.y - METRO_DOT / 2,
            width: METRO_DOT,
            height: METRO_DOT,
        };
        const others = (twins.get(key) ?? []).filter((id) => id !== station.id);
        patches.set(station.id, {
            rect: union(square, label),
            textBox: label,
            bare: true,
            outline: null,
            cornerRadius: 4,
            style: withoutFill(node.style),
            textStyle: { ...node.textStyle, fontWeight: interchange ? 700 : node.textStyle.fontWeight },
            glyph: interchange
                ? [
                    {
                        kind: 'circle',
                        cx: dot.x,
                        cy: dot.y,
                        r: 8,
                        fill: 'surface',
                        stroke: 'ink',
                        strokeWidth: 3,
                    },
                ]
                : [
                    {
                        kind: 'circle',
                        cx: dot.x,
                        cy: dot.y,
                        r: 5.5,
                        fill: 'surface',
                        stroke: colour,
                        strokeWidth: 3,
                    },
                ],
            ...(others.length === 0 ? {} : { twins: others }),
        });
        // The same station written under another line: drawn as the one above, still a node to select.
        for (const id of others) {
            const twin = tools.byId.get(id);
            const twinNode = tools.doc(id);
            if (twin === undefined || twinNode === undefined)
                continue;
            const own = stationDot(twinNode);
            patches.set(id, {
                rect: {
                    x: own.x - METRO_DOT / 2,
                    y: own.y - METRO_DOT / 2,
                    width: METRO_DOT,
                    height: METRO_DOT,
                },
                lines: [],
                bare: true,
                outline: null,
                style: withoutFill(twin.style),
                glyph: [],
                twins: [station.id, ...others.filter((other) => other !== id)],
            });
        }
    }
}
//# sourceMappingURL=structures.js.map